import React, { useEffect, useRef } from 'react';

// Powder-like Particle Cursor
// - Small, powder-like particles (no big drops)
// - Colors: White, Blue, Sky Blue, #A656F7, #62A3FA
// - No forward acceleration from the mouse
// - High intensity but highly optimized drawing

const ParticleCursor: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<any[]>([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Disable on touch devices
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let rafId: number;
    let lastTime = performance.now();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize, { passive: true });
    resize();

    // Specific requested colors: White, Blue, Sky Blue, #A656F7, #62A3FA
    // Converted to RGB for easy alpha manipulation
    const COLORS = [
      '255, 255, 255', // White
      '59, 130, 246',  // Blue (tailwind blue-500)
      '135, 206, 235', // Sky Blue
      '166, 86, 247',  // #A656F7 (Purple)
      '98, 163, 250',  // #62A3FA (Light Blue)
    ];

    const handleMouseMove = (e: MouseEvent) => {
      const x = e.clientX;
      const y = e.clientY;
      
      const lastX = mouseRef.current.x || x;
      const lastY = mouseRef.current.y || y;
      
      const dist = Math.hypot(x - lastX, y - lastY);
      
      // Spawn particles smoothly along the movement vector
      if (dist > 1 && !isInitialLoadRef.current) {
        // High intensity: spawn more particles per pixel moved
        // Using a high cap (up to 50) and a higher multiplier
        const count = Math.min(Math.floor(dist * 1.5), 50);
        
        for (let i = 0; i < count; i++) {
          const interpX = lastX + (x - lastX) * (i / count);
          const interpY = lastY + (y - lastY) * (i / count);
          
          // Powder scatter: spread them out slightly around the cursor
          // Tighten the scatter slightly for a denser core trail
          const scatterX = (Math.random() - 0.5) * 6;
          const scatterY = (Math.random() - 0.5) * 6;
          
          particlesRef.current.push(
            new Particle(interpX + scatterX, interpY + scatterY)
          );
        }
      }
      
      mouseRef.current.x = x;
      mouseRef.current.y = y;
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    class Particle {
      x: number; y: number;
      vx: number; vy: number;
      life: number; maxLife: number;
      size: number;
      color: string;

      constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        
        // Random drift ONLY - no forward momentum from the mouse cursor
        const angle = Math.random() * Math.PI * 2;
        // Very slow drift speed so it looks like floating powder
        const speed = Math.random() * 0.6 + 0.05;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        
        // Longer life for a lingering powder effect
        this.maxLife = Math.random() * 40 + 40; 
        this.life = this.maxLife;
        
        // Slightly larger powder-like sizes (1.0 to 3.0 pixels)
        this.size = Math.random() * 2.0 + 1.0; 
        
        this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
      }

      update(dt: number) {
        this.x += this.vx * dt;
        this.y += this.vy * dt;
        
        // Friction: slow down the drift quickly
        this.vx *= 0.94;
        this.vy *= 0.94;
        
        // Gentle upward thermal drift (like fine powder/smoke)
        this.vy -= 0.01 * dt;

        // Die over time
        this.life -= 1 * dt;
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        
        // Optimize: skip extremely faint particles to save rendering time
        const rawAlpha = Math.max(0, this.life / this.maxLife);
        if (rawAlpha < 0.05) return;
        
        const opacity = rawAlpha * 0.9; 

        // Highly optimized drawing
        ctx.fillStyle = `rgba(${this.color}, ${opacity})`;
        ctx.beginPath();
        // Use rect instead of arc for micro-particles (sub-1.2px) to save rendering time
        if (this.size < 1.2) {
          ctx.fillRect(this.x, this.y, this.size, this.size);
        } else {
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
        }
        
        // Add a very subtle, tight glow only to the brightest particles to save GPU
        if (rawAlpha > 0.4) {
          ctx.fillStyle = `rgba(${this.color}, ${opacity * 0.25})`;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size * 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    const animate = (now: number) => {
      rafId = requestAnimationFrame(animate);
      
      const dt = Math.min((now - lastTime) / 16.67, 3); // normalized delta time, max cap
      lastTime = now;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Screen blend mode for that magical powder glow
      ctx.globalCompositeOperation = 'screen';
      
      // Significantly increase cap for maximum intensity without crashing
      const MAX_PARTICLES = 1500;
      if (particlesRef.current.length > MAX_PARTICLES) {
        // Fast truncation from the beginning (oldest particles)
        particlesRef.current = particlesRef.current.slice(particlesRef.current.length - MAX_PARTICLES);
      }

      for (let i = particlesRef.current.length - 1; i >= 0; i--) {
        const p = particlesRef.current[i];
        p.update(dt);
        if (p.life <= 0) {
          // Fast array removal from end
          particlesRef.current[i] = particlesRef.current[particlesRef.current.length - 1];
          particlesRef.current.pop();
        } else {
          p.draw(ctx);
        }
      }
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ 
        zIndex: 999999, 
        mixBlendMode: 'screen', 
        background: 'transparent'
      }}
    />
  );
};

export default ParticleCursor;
